import React, { useState } from 'react';
import { Fade, Grow } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import LoadingBarChart from 'components/generic-dashboards/loading/LoadingBarChart';
import { MissingData } from 'components/misc/MissingData';
import BarChart from './BarChart';
import Modal from 'components/misc/Modal';

interface Props {
    topic?: string;
    dashboard?: string;
    title?: string;
    subtitle?: string | null;
    tooltipContent?: JSX.Element;
    dynamicData?: {
        title: string;
        data: any[] | null;
        dataLoading: boolean;
        hideData?: boolean;
        xKey: string;
        colorSet: {
            primary: string;
            secondary: string;
        };
    }[];
}

const BarChartCollection = ({
    title = 'Title',
    subtitle = 'Subtitle',
    tooltipContent,
    dynamicData = [],
}: Props) => {
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    return (
        <Fade in={true}>
            <div>
                <div className="flex lg:flex-row flex-col justify-between">
                    <div className="text-xl">{title}</div>
                    <div className="flex flex-row items-center">
                        <div className="text-lg font-light mr-2">{subtitle}</div>

                        {tooltipContent && (
                            <div
                                className="hover: cursor-pointer"
                                onClick={() => setModalOpen(true)}
                            >
                                <HelpIcon color="info" />
                            </div>
                        )}
                    </div>
                </div>

                <Modal
                    title={title}
                    open={modalOpen}
                    width={50}
                    onClose={() => setModalOpen(false)}
                >
                    {tooltipContent}
                </Modal>

                <hr className="h-0.5 my-3 bg-slate-100 rounded" />

                <div
                    className={`flex flex-col lg:grid gap-4`}
                    style={{
                        gridTemplateColumns:
                            dynamicData.length > 1
                                ? `repeat(${dynamicData.length}, minmax(0, 1fr))`
                                : undefined,
                    }}
                >
                    {dynamicData.map((entry, idx) => {
                        if (entry.dataLoading) {
                            return <LoadingBarChart key={idx} />;
                        } else if (entry.dataLoading && !entry.data && !entry.hideData) {
                            return <MissingData key={idx} />;
                        } else if (entry.data) {
                            return (
                                <div className="col-span-1" key={idx}>
                                    <Grow in={true}>
                                        <div>
                                            <BarChart
                                                title={entry.title}
                                                primaryColor={entry.colorSet.primary}
                                                secondaryColor={entry.colorSet.secondary}
                                                data={[...entry.data].slice(0, 12).reverse()}
                                                dataKey="count"
                                                xKey={entry.xKey}
                                                yKey="count"
                                                average
                                            />
                                        </div>
                                    </Grow>
                                </div>
                            );
                        } else {
                            return <React.Fragment key={idx} />;
                        }
                    })}
                </div>
            </div>
        </Fade>
    );
};

export { BarChartCollection };
